import { Component, DestroyRef, OnInit, PLATFORM_ID, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { V1LegacyHandoff, APP_PATHS, LegacyHandoffService } from '@core/routing';
// Direct path, not the `core/language` barrel — that would pull the route-scoped
// LanguageFacade/Store into the initial bundle.
import { LANG_QUERY_PARAM, parseLangParam } from '@core/language/lang-param';
import { ReferralCodeService } from '@core/auth/application/referral-code.service';

/**
 * App (`app-root`) — the bootstrap root.
 *
 * v1 is decommissioned (docs/v2-isolation/), so this component only:
 *   1. hosts the router-outlet /v2 renders into, and
 *   2. resolves the external legacy deep links still live in the wild
 *      (old SMS/email/push links) and hands them off to their v2 destination.
 *
 * The computation lives in {@link LegacyHandoffService}; this component owns
 * only WHEN it runs and its one-shot guard state.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly destroy$ = inject(DestroyRef);
  private readonly handoff = inject(LegacyHandoffService);
  private readonly referral = inject(ReferralCodeService);

  /** Entry url carried `?isEmergency` / `?isMedicine` → `/care?crisis=1` / `/medicine`. */
  private urgentHandoff = false;

  /**
   * The entry url's resolved legacy-PATH deep link, if it was one. `/appointments`
   * and `/book-appointment` are v1 screens the redesign replaced:
   *
   *   /appointments?isEmergency=true&lang=ar → ar + Care CR5 «جلساتي»
   *   /book-appointment?lang=en              → en + Care CR2 «المستشارون»
   */
  private legacyHandoff: V1LegacyHandoff | null = null;

  /**
   * Up from detecting a {@link legacyHandoff} until its /v2 navigation settles.
   * The template holds the router-outlet back meanwhile, so the entry url's own
   * route never flashes before the handoff navigates.
   */
  protected readonly legacyHandoffPending = signal<boolean>(false);

  /** Guards {@link runLegacyHandoff} to a single navigation per visit. */
  private legacyHandoffStarted = false;

  constructor() {
    // Read both flags off the entry url — the root ActivatedRoute snapshot is not
    // populated this early. The PATH mapping resolves first and wins, so
    // `/appointments?isEmergency=true` (the sessions list) is not swallowed by the
    // path-agnostic urgent-care branch below.
    const entryHandoff = this.handoff.resolveEntryHandoff(
      this.document.location?.pathname,
      this.document.location?.search,
    );
    this.legacyHandoff = entryHandoff.legacyHandoff;
    this.legacyHandoffPending.set(!!this.legacyHandoff);
    this.urgentHandoff = entryHandoff.urgentHandoff;

    // Apply `?lang=ar|en` here — the last moment before the lazy /v2 subtree boots
    // and reads it — so the redesign paints in the requested language on its first
    // frame instead of flipping or reloading afterwards.
    this.handoff.applyEntryLangParam(this.legacyHandoff, this.urgentHandoff);

    // BE11 — keep an invite code from the entry link (`?ref=` · `?r=` · `/invite?code=`)
    // until sign-up; OTP verify sends it once as `referral_code`.
    if (isPlatformBrowser(this.platformId)) {
      this.referral.captureFromUrl(this.document.location?.pathname, this.document.location?.search);
    }
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return; // SSR-safe

    // `takeUntilDestroyed` so the listener dies with the root component.
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroy$)).subscribe(params => {
      // ── legacy PATH deep links → their v2 replacement ───────────────────────
      // Resolved from the entry url in the constructor, so this fires once per
      // visit. Runs before the urgent-care branch: the path is the more specific
      // instruction.
      if (this.legacyHandoffPending()) {
        this.runLegacyHandoff();
        return;
      }

      // ── `?lang=` arriving on /v2 AFTER bootstrap ────────────────────────────
      // The entry one was applied + stripped in the constructor, so this is an
      // in-SPA arrival: same state the topbar button handles, so do what it does —
      // persist, strip (stops the reloaded url re-firing this), then hard-reload.
      const arrivingLang = parseLangParam(params[LANG_QUERY_PARAM]);
      if (arrivingLang && this.handoff.isPrimaryVisit(this.router.url)) {
        const changed = this.handoff.applyLanguageChange(arrivingLang);
        this.handoff.stripLangParamFromLocation();
        // Return only when the page is actually going away — a no-op
        // re-application must fall through to the urgent-care branch, for a url
        // carrying both params.
        if (changed) {
          this.document.location?.reload();
          return;
        }
      }

      // ── legacy urgent-care deep links → their redesigned surface ────────────
      //   ?isEmergency → `/care?crisis=1` — CarePage `enterCrisis()` → CR10 form.
      //   ?isMedicine  → `/medicine`      — `features/medicine`, the port of v1's
      //                                     medicine mode.
      //
      // `isMedicine` is checked first: a link carrying both names the narrower
      // journey. Navigating to a fresh url drops the consumed params, and
      // `replaceUrl` keeps the trigger url out of back history. A guest is bounced
      // by `authGuard` with `?returnUrl=`, so the surface opens once authenticated.
      if (this.handoff.isUrgentCareFlag(params['isMedicine'])) {
        void this.router.navigate([APP_PATHS.MEDICINE], { replaceUrl: true });
      } else if (this.handoff.isUrgentCareFlag(params['isEmergency'])) {
        void this.router.navigate([APP_PATHS.CARE], {
          queryParams: { crisis: 1 },
          replaceUrl: true,
        });
      }
    });
  }

  /**
   * Perform the resolved {@link legacyHandoff}: redirect into /v2.
   *
   * `?lang=` was already applied in the constructor, so the redesigned surface
   * paints in the requested language on its first frame. The fresh url drops the
   * consumed legacy params, and `replaceUrl` keeps the trigger url out of back
   * history. Guests are bounced by `authGuard` with `?returnUrl=`.
   */
  private runLegacyHandoff(): void {
    const handoff = this.legacyHandoff;
    // One-shot: `queryParams` re-emits while the redirect is in flight.
    if (!handoff || this.legacyHandoffStarted || !isPlatformBrowser(this.platformId)) return;
    this.legacyHandoffStarted = true;

    void this.router
      .navigate([...handoff.commands], {
        queryParams: handoff.queryParams,
        replaceUrl: true,
      })
      .finally(() => this.legacyHandoffPending.set(false));
  }
}
